/**
 * Google Ads — gtag.js conversion tracking for the TrailerCast app.
 *
 * Sister module to utils/redditPixel.js. Same flow:
 *   - Marketing site (trailercast.io) handles top-of-funnel pageviews
 *   - This module handles bottom-of-funnel conversions:
 *       trackTrialStarted() — new account created (signup step 1)
 *       trackPurchase()     — Stripe Checkout success (/billing/success)
 *
 * Required env var (Vercel app project):
 *   REACT_APP_GOOGLE_ADS_ID — global Google Ads tag id, format
 *                             "AW-XXXXXXXXXX". Same value as the
 *                             website's NEXT_PUBLIC_GOOGLE_ADS_ID.
 *
 * Per-conversion env vars (set as you create each conversion in
 * Google Ads → Goals → Conversions; the value is the `send_to`
 * string from each conversion's tag setup, format
 * "AW-XXXXXXXXXX/AbC-D_efG-h12_34-567"):
 *   REACT_APP_GADS_CONVERSION_TRIAL_SEND_TO    — for Trial Started
 *   REACT_APP_GADS_CONVERSION_PURCHASE_SEND_TO — for Subscription Started
 *
 * Until each per-conversion env var is set, the corresponding track*
 * function is a no-op. Means we can ship the global tag now (enables
 * remarketing + general PageView attribution) and wire up the
 * conversion-specific events later without another deploy — just
 * paste the env vars and Vercel redeploys.
 *
 * Consent posture: same as redditPixel.js — the app currently doesn't
 * surface a cookie banner because the user has agreed to terms when
 * they signed up. Reconsider if we ever target consumers or EU-heavy
 * buyers.
 *
 * 2026-05-05: env vars wired into the trailercst-frontend Vercel
 * project (REACT_APP_GOOGLE_ADS_ID + the two CONVERSION_*_SEND_TO
 * vars). Earlier deploy reused the build cache and didn't pick them
 * up; this commit forces a fresh build so the gtag loader script
 * actually starts firing on production.
 */

import { shouldSuppress } from './adSuppression';

const TAG_ID = (process.env.REACT_APP_GOOGLE_ADS_ID || '').trim();
const TRIAL_SEND_TO = (process.env.REACT_APP_GADS_CONVERSION_TRIAL_SEND_TO || '').trim();
const PURCHASE_SEND_TO = (process.env.REACT_APP_GADS_CONVERSION_PURCHASE_SEND_TO || '').trim();

let initialized = false;

/**
 * Inject the gtag.js loader + initialize. Snippet is verbatim from
 * Google's installation guide — leave as-is so future Google changes
 * to the snippet don't drift from our copy.
 */
function injectScript() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.gtag) return; // already injected (e.g. dev hot-reload)

  // 1. Bootstrap the gtag function + dataLayer queue. This must be
  //    available SYNCHRONOUSLY before the loader script finishes —
  //    callers that fire events immediately after init() rely on
  //    queueing into dataLayer.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', TAG_ID);

  // 2. Load the actual gtag.js file. Async so it doesn't block.
  const s = document.createElement('script');
  s.src = `https://www.googletagmanager.com/gtag/js?id=${TAG_ID}`;
  s.async = true;
  document.head.appendChild(s);
}

/**
 * Initialize Google Ads tag. Call once on app boot. Subsequent calls
 * are harmless but wasteful.
 */
function init() {
  if (!TAG_ID) return;
  if (initialized) return;
  if (shouldSuppress()) return;
  injectScript();
  initialized = true;
}

/**
 * Fire the Trial Started conversion event. Use after a successful
 * /api/auth/signup response (account row created).
 */
function trackTrialStarted() {
  if (!TAG_ID || !TRIAL_SEND_TO) return; // silently no-op until env vars set
  if (shouldSuppress()) return;
  if (!initialized) init();
  try {
    window.gtag('event', 'conversion', {
      send_to: TRIAL_SEND_TO,
      value: 50.0, // USD; matches the conversion-action default in Google Ads
      currency: 'USD',
    });
  } catch (err) {
    console.warn('[googleAds] trackTrialStarted failed:', err.message);
  }
}

/**
 * Fire the Subscription Started conversion event. Use on
 * /billing/success after Stripe Checkout completes.
 *
 * Optional payload:
 *   value    — order amount in USD per seat ($79 monthly, $708 annual).
 *              Mirrors what we send to Reddit's Purchase event.
 *   currency — defaults to 'USD'.
 */
function trackPurchase({ value, currency = 'USD' } = {}) {
  if (!TAG_ID || !PURCHASE_SEND_TO) return;
  if (shouldSuppress()) return;
  if (!initialized) init();
  try {
    const payload = { send_to: PURCHASE_SEND_TO, currency };
    if (typeof value === 'number') payload.value = value;
    window.gtag('event', 'conversion', payload);
  } catch (err) {
    console.warn('[googleAds] trackPurchase failed:', err.message);
  }
}

const googleAds = { init, trackTrialStarted, trackPurchase };
export default googleAds;
